import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref } from 'vue-property-decorator';

@Component({
  name: 'ed-menu'
})
class EdMenu extends Vue {

  @Prop({ default: false }) offsetX!: boolean;
  @Prop({ default: false }) offsetY!: boolean;
  @Prop({ default: 0 }) nudgeBottom!: number;
  @Prop({ default: true }) closeOnContentClick!: boolean;
  @Prop({ default: 10000 }) zIndex!: number;
  @Prop({ default: true }) canAppendToBody: boolean;
  @Prop({ default: false }) disabled: boolean;
  @Prop({default: false}) openOnHover: boolean;
  @Prop({default: false}) attach: boolean;

  isOpen = false;
  menuStyles: Record<string, string> = {};
  touchMoved: boolean = false;
  _shouldClose: boolean = false;
  private static readonly SAFETY_MARGIN: number = 8;
  private static readonly EXTRA_MARGIN: number = 80;

  @Ref('menuWrapper') readonly menuWrapper!: HTMLElement;
  @Ref('menuContent') readonly menuContent!: HTMLElement;

  toggleMenu(): void {
    if(!this.disabled && !this.openOnHover) this.isOpen = !this.isOpen;
    if(!this.disabled && this.openOnHover) this.isOpen = true;
    if (this.isOpen) {
      this.updatePosition();
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.updatePosition();
        });
      });
    }
  }

  updatePosition(): void {
    if (!this.menuWrapper || !this.menuContent) return;

    const triggerRect = this.menuWrapper.getBoundingClientRect();
    const menuRect = this.menuContent.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    let top = triggerRect.bottom + this.nudgeBottom;
    let left = triggerRect.left;

    const existingMenu = document.body.querySelector('.ed-menu__content');
    const bodyAppended = existingMenu.parentElement === document.body;

    if(!bodyAppended && this.canAppendToBody) {
      document.body.appendChild(this.menuContent);
    }
    
    if (this.offsetY) {
      top = triggerRect.top;
    }

    if (windowHeight - triggerRect.bottom < menuRect.height) {
      // Not enough space below, position above
      if (triggerRect.top >= menuRect.height) {
        top = triggerRect.top - menuRect.height - this.nudgeBottom;
      }
    }

    if (this.offsetX) {
      left = triggerRect.right;
      if (left + menuRect.width > windowWidth) {
        // If not enough space on right, position on left
        left = triggerRect.left - menuRect.width;
      }
    } else {
      // Doesn't overflow right edge
      if (left + menuRect.width > windowWidth) {
        left = windowWidth - menuRect.width - EdMenu.SAFETY_MARGIN;
      }
      // Doesn't overflow left edge
      if (left < 0) {
        left = EdMenu.SAFETY_MARGIN;
      }
    }
    // Calculate min height according to menu position.
    const availableHeight = windowHeight - top - EdMenu.SAFETY_MARGIN;
    let maxHeight = 'auto';
    if(!this.attach)
      maxHeight = `${Math.min(availableHeight, windowHeight - EdMenu.EXTRA_MARGIN)}px`;

    this.menuStyles = {
      top: `${top}px`,
      left: `${left}px`,
      zIndex: this.zIndex.toString(),
      maxHeight: maxHeight,
    };
  }

  onClickOutside(event: Event): void {
    if (!this.isOpen || !this.menuContent || this.menuContent.contains(event.target as Node)) return;
    this.isOpen = false;
  }

  onContentClick(): void {
    if (this.closeOnContentClick) {
      this.isOpen = false;
    }
  }

  onResize(): void {
    if (this.isOpen) {
      this.updatePosition();
    }
  }

  forceClose(): void {
    this.isOpen = false;
  }

  // Close menu Mobile events
  onTouchStart(event: Event): void {
    this.touchMoved = false;

    const tappedOutside =
      !this.menuContent.contains(event.target as Node) &&
      !this.menuWrapper.contains(event.target as Node);

    if (tappedOutside) {
      this._shouldClose = true;
    } else {
      this._shouldClose = false;
    }
  }

  onTouchMove(): void {
    this.touchMoved = true;
  }

  onTouchEnd(event: Event): void {
    if (this._shouldClose && !this.touchMoved) {
      this.onClickOutside(event);
    }
  }

  // Handle open on hover event
  get activatorListeners() {
    if (this.openOnHover) {
      return {
        mouseenter: () => this.toggleMenu(),
        mouseleave: () => this.isOpen = false
      };
    }
    return {
      click: this.toggleMenu
    };
  }

  get menuListeners() {
    if (this.openOnHover) {
      return {
        mouseenter: () => this.toggleMenu(),
        mouseleave: () => this.isOpen = false
      };
    }
    return {};
  }

  mounted() {
    window.addEventListener('resize', this.onResize);
    if(this.attach)
      window.addEventListener('scroll', this.onResize, true);

    if (!('ontouchstart' in window)) {
      document.addEventListener('pointerdown', this.onClickOutside);      
    } else {
      // Touch/mobile
      document.addEventListener('touchstart', this.onTouchStart, true);
      document.addEventListener('touchmove', this.onTouchMove, true);
      document.addEventListener('touchend', this.onTouchEnd, true);
    }
  }

  beforeDestroy() {
    document.removeEventListener('pointerdown', this.onClickOutside);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onResize, true);
    document.removeEventListener('touchstart', this.onTouchStart, true);
    document.removeEventListener('touchmove', this.onTouchMove, true);
    document.removeEventListener('touchend', this.onTouchEnd, true);
    
    // Make sure the element is removed from the body
    if (this.menuContent?.parentNode === document.body) {
      document.body.removeChild(this.menuContent);
    }
  }
}

export default Vue.component('ed-menu', EdMenu);
